/**
 * Per-language resolution of a venue's free-text descriptors.
 *
 * The base columns (`tagline`, `description`, `cuisine`) are the primary /
 * English values; the `*_ro` columns hold optional Romanian variants. The
 * consumer app's EN/RO toggle picks which to show — RO falls back to the base
 * value whenever the Romanian field is left blank.
 */

import type { Lang } from "@/i18n/translations";
import type { Restaurant } from "@/types/restaurant";

/** Pick the RO variant when lang==="ro" and it's non-blank, else the base value. */
export function pickLocalized(
  base: string | undefined,
  ro: string | undefined,
  lang: Lang,
): string | undefined {
  return lang === "ro" && ro?.trim() ? ro : base;
}

/**
 * Resolve the localized free-text descriptor(s) for the active language.
 *
 * Only `description` remains here — `cuisine` and `tagline` moved to the
 * structured taxonomy (`cuisine_slugs` / `tag_slugs`), resolved via
 * `@/lib/taxonomyLabels`. `pickLocalized` is kept for the description pair.
 */
export function localizeRestaurant(r: Restaurant, lang: Lang) {
  return {
    description: pickLocalized(r.description, r.description_ro, lang),
  };
}
